import { CKEditor } from '@ckeditor/ckeditor5-react';
import React, { useCallback, useEffect, useRef } from 'react';
import {
	AttachmentPreviewList,
	SendButton,
	useChannelActionContext,
	useChannelStateContext,
	useMessageInputContext,
} from 'stream-chat-react';

import CloseIcon from '@mui/icons-material/Close';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { Box, IconButton, Typography } from '@mui/material';
// eslint-disable-next-line import/no-extraneous-dependencies
import { baseEditorConfig } from 'admin/src/components/Form/TextEditor/editorConfig';
import { ClassicEditor, List, ListProperties } from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';

import { AttachmentsPreviewListWrapper } from '@common/modules/Chat/CustomChatInput/styled';

import { buildStreamAttachments } from '../MassMailingModal';

interface CustomChatInputProps {
	hideSendButton?: boolean;
	onChange?: (payload: { text: string; attachments: any[] }) => void;
}

function CustomChatInput({ hideSendButton = false, onChange }: CustomChatInputProps) {
	const { text, setText, uploadNewFiles, handleSubmit, imageUploads, fileUploads } =
		useMessageInputContext();

	const { quotedMessage } = useChannelStateContext();
	const { setQuotedMessage } = useChannelActionContext();

	const fileInputRef = useRef<HTMLInputElement>(null);

	const imageUploadsArray = Object.values(imageUploads || {});
	const fileUploadsArray = Object.values(fileUploads || {});

	const disabled =
		imageUploadsArray.some((u) => u.state === 'uploading') ||
		fileUploadsArray.some((u) => u.state === 'uploading');

	const send = async () => {
		if (disabled) return;

		await handleSubmit();
		setText('');
	};

	const openFileDialog = () => {
		fileInputRef.current?.click();
	};

	const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!e.target.files?.length) return;
		uploadNewFiles(Array.from(e.target.files));
		e.target.value = '';
	};

	const onDrop = useCallback(
		(e: React.DragEvent) => {
			e.preventDefault();
			if (!e.dataTransfer.files?.length) return;
			uploadNewFiles(Array.from(e.dataTransfer.files));
		},
		[uploadNewFiles],
	);

	useEffect(() => {
		if (!onChange) return;

		onChange({
			text,
			attachments: buildStreamAttachments({
				imageUploads,
				fileUploads,
			}),
		});
	}, [text, imageUploads, fileUploads, onChange]);

	return (
		<div style={{ width: '100%' }}>
			<AttachmentsPreviewListWrapper>
				<AttachmentPreviewList />
			</AttachmentsPreviewListWrapper>

			{quotedMessage && (
				<Box
					sx={{
						padding: '6px 10px',
						marginBottom: 8,
						background: '#f3f4f6',
						borderLeft: '3px solid #6366f1',
						fontSize: 13,
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						gap: 8,
					}}
				>
					<Box
						sx={{
							overflow: 'hidden',
							whiteSpace: 'nowrap',
							textOverflow: 'ellipsis',
						}}
					>
						<strong>{quotedMessage.user?.name}:</strong>{' '}
						<Typography
							dangerouslySetInnerHTML={{
								__html: quotedMessage.text ?? '',
							}}
						/>
					</Box>

					{/* eslint-disable-next-line react/button-has-type */}
					<IconButton
						onClick={() => setQuotedMessage(undefined)}
						sx={{
							border: 'none',
							background: 'transparent',
							cursor: 'pointer',
							fontSize: 16,
							lineHeight: 1,
						}}
					>
						<CloseIcon />
					</IconButton>
				</Box>
			)}

			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: '12px',
					width: '100%',
				}}
			>
				<Box
					onClick={openFileDialog}
					onDrop={onDrop}
					onDragOver={(e) => e.preventDefault()}
					sx={{
						width: 32,
						height: 32,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						cursor: 'pointer',
					}}
				>
					<ImageOutlinedIcon />
				</Box>

				<input ref={fileInputRef} type="file" multiple hidden onChange={onFileChange} />

				<Box
					sx={{
						flex: 1,
						'& .ck.ck-content.ck-editor__editable': {
							maxHeight: 250,
							overflowY: 'auto',
						},
					}}
				>
					<CKEditor
						editor={ClassicEditor}
						data={text}
						config={{
							...baseEditorConfig,
							toolbar: {
								items: ['bold', 'italic', 'underline', 'link', 'bulletedList', 'numberedList'],
							},
							plugins: [...baseEditorConfig.plugins, List, ListProperties],
							menuBar: { isVisible: false },
							placeholder: 'Write a message...',
						}}
						onChange={(_, editor) => {
							setText(editor.getData());
						}}
					/>
				</Box>

				{!hideSendButton && <SendButton sendMessage={send} disabled={disabled} />}
			</Box>
		</div>
	);
}

export default CustomChatInput;
